import apiClient from "../../../api/apiClient";
import { UnifiedResponse } from "../../../models/Response/unifiedResponse";
import {
  AvailableDepositBonusesResponse,
  DepositFlowResponse,
  DepositTypeListResponse,
  UserDepositSummaryResponse,
  UserMobileNumberDetailResponse,
  UserPendingDepositsResponse,
  DepositConfigurationResponse,
  ValidateBonusEligibilityResponse,
  DepositCreateResponse,
  UserDepositValidationResponse,
  TopRecommendedTransferMethodsResponse,
  UserRecentUsedDepositTypesResponse,
  DepositPaymentMethodListResponse
} from "../models/response";
import {
  AvailableDepositBonusesRequest,
  ValidateBonusEligibilityRequest,
  AbcPaymentDepositInitiateReuest,
  DepositCreateRequest,
  AbcDepositCreateRequest,
  GatewayHubDepositCreateRequest,
  DragonDepositCreateRequest,
  DepositTypeListRequest,
  PayzEasyDepositInitiateRequest,
} from "../models/request";

const depositApi = {
  getDepositFlowDetails: async (): Promise<
    UnifiedResponse<DepositFlowResponse>
  > => {
    const response = await apiClient.get<UnifiedResponse<DepositFlowResponse>>(
      `/select/deposit/flow`,
      {}
    );
    return response.data;
  },

  getDepositTypes: async (
    isActive: boolean
  ): Promise<UnifiedResponse<DepositTypeListResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositTypeListResponse>
    >(`/payment/6ball_payments_retrieve/list`, isActive, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  },

  getUserDepositSummary: async (): Promise<
    UnifiedResponse<UserDepositSummaryResponse>
  > => {
    const response = await apiClient.get<
      UnifiedResponse<UserDepositSummaryResponse>
    >(`/payment/user_deposit_withdraw/select`, {});
    return response.data;
  },

  getUserMobileDetail: async (): Promise<
    UnifiedResponse<UserMobileNumberDetailResponse>
  > => {
    const response = await apiClient.get<
      UnifiedResponse<UserMobileNumberDetailResponse>
    >(`/retrive/users/mobile_details`, {});
    return response.data;
  },

  getUserPendingDeposits: async (): Promise<
    UnifiedResponse<UserPendingDepositsResponse>
  > => {
    const response = await apiClient.get<
      UnifiedResponse<UserPendingDepositsResponse>
    >(`/payment/deposit/check_payment`, {});
    return response.data;
  },

  getAvailableDepositBonuses: async (
    request: AvailableDepositBonusesRequest
  ): Promise<UnifiedResponse<AvailableDepositBonusesResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<AvailableDepositBonusesResponse>
    >(`/bonus/retrieve`, request, {});
    return response.data;
  },

  getDepositConfiguration: async (): Promise<
    UnifiedResponse<DepositConfigurationResponse>
  > => {
    const response = await apiClient.get<
      UnifiedResponse<DepositConfigurationResponse>
    >(`/settings/master_settings/list`);
    return response.data;
  },

  validateBonusEligibility: async (
    request: ValidateBonusEligibilityRequest
  ): Promise<UnifiedResponse<ValidateBonusEligibilityResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<ValidateBonusEligibilityResponse>
    >(`/bonus/validation`, request, {});
    return response.data;
  },

  abcDepositInitiate: async (
    request: AbcPaymentDepositInitiateReuest
  ): Promise<UnifiedResponse<DepositCreateResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositCreateResponse>
    >("/abc_payment/deposits/initiate", request, {});
    return response.data;
  },

  abcCryptoDepositInitiate: async (
    request: AbcPaymentDepositInitiateReuest
  ): Promise<UnifiedResponse<DepositCreateResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositCreateResponse>
    >("/abc_crypto_payment/crypto/deposit/initiate", request, {});
    return response.data;
  },

  validateUserDepositRequest: async (
    amount: number
  ): Promise<UnifiedResponse<UserDepositValidationResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<UserDepositValidationResponse>
    >("/payment/users_offline_deposit/validate", { amount }, {});
    return response.data;
  },

  submitDeposit: async (
    request: DepositCreateRequest
  ): Promise<UnifiedResponse<DepositCreateResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositCreateResponse>
    >("/payment/deposit/insert", request, {});
    return response.data;
  },

  abcDepositSubmit: async (
    request: AbcDepositCreateRequest
  ): Promise<UnifiedResponse<any>> => {
    const response = await apiClient.post<
      UnifiedResponse<any>
    >("/abc_payment/deposits/submit_transaction", request, {});
    return response.data;
  },

  dragonDepositSubmit: async (
    request: DragonDepositCreateRequest
  ): Promise<UnifiedResponse<DepositCreateResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositCreateResponse>
    >("/dragon/payment/create/deposit", request, {});
    return response.data;
  },

  getGatewayHubUserId: async (): Promise<UnifiedResponse<number>> => {
    const response = await apiClient.post<
      UnifiedResponse<number>
    >("/retrive/payment_gatewayhub/userid", {});
    return response.data;
  },

  gatewayHubDepositSubmit: async (
    request: GatewayHubDepositCreateRequest
  ): Promise<UnifiedResponse<DepositCreateResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositCreateResponse>
    >("/gatewayhub_pg/deposit/initiate", request, {});
    return response.data;
  },

  payzEasyDepositSubmit: async (
    request: PayzEasyDepositInitiateRequest
  ): Promise<UnifiedResponse<DepositCreateResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositCreateResponse>
    >("/payzeasy/deposit/initiate", request, {});
    return response.data;
  },

  getTopRecommendedTransferMethods: async (): Promise<UnifiedResponse<TopRecommendedTransferMethodsResponse>> => {
    const response = await apiClient.get<
      UnifiedResponse<TopRecommendedTransferMethodsResponse>
    >("/payment/top_recommended_transfer_methods/list", {});
    return response.data;
  },

  getUserRecentUsedDepositTypes: async (): Promise<UnifiedResponse<UserRecentUsedDepositTypesResponse>> => {
    const response = await apiClient.get<
      UnifiedResponse<UserRecentUsedDepositTypesResponse>
    >("/payment/user_recent_used_deposit_type/retrieve", {});
    return response.data;
  },

  getDepositTypeWithAbcBankDetails: async (
    request: DepositTypeListRequest
  ): Promise<UnifiedResponse<DepositTypeListResponse>> => {
    const response = await apiClient.post<
      UnifiedResponse<DepositTypeListResponse>
    >("/payment/6ball_payments_retrieve_abc/list", request, {});
    return response.data;
  },

  getDepositPaymentMethod: async (): Promise<UnifiedResponse<DepositPaymentMethodListResponse>> => {
    const response = await apiClient.get<
      UnifiedResponse<DepositPaymentMethodListResponse>
    >("/payment/deposit_type_method_retrive/list", {});
    return response.data;
  },
};

export default depositApi;
